import { getStoredRealm } from "@/utils/auth-storage";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { Box, Button, Grid, IconButton, InputAdornment } from "@mui/material";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { uniqBy } from "lodash";
import { useEffect, useState } from "react";
import { TIME_ZONE_WITH_OFFSET_LIST, convertToUTC } from "../constants";
import { useSlotList } from "../queries/use-get-slots";
import { GetProviderSlotsAvailabilityModeEnum } from "../services";
import { formatDate, formatTime, isToday } from "../utils/date-time-utility";
import Flex from "./Flex";
import Input from "./Input";
import { ScrollArea } from "./ScrollBar";
import Text from "./Text";

interface TimeSlotPickerProps {
  formik: any;
  // label: string;
  selectedDateTime: dayjs.Dayjs | null;
  onChange: (newDate: dayjs.Dayjs | null) => void;
  openDateCalendar: boolean;
  toggleDateCalendar: () => void;
  providerUuid: string;
  selectedLocation?: string;
  appointmentType: string;
  mode?: GetProviderSlotsAvailabilityModeEnum;
  type: "new" | "rescheduled";
  isTouched: any;
  isRescheduled?: boolean;
  bookedSlot?: any;
}

function bookedSlot(arr1: any[], arr2: any[]): any {
  const set = new Set(arr2?.map((item) => JSON.stringify(item)));

  return arr1?.filter((item) => !set.has(JSON.stringify(item)))?.[0] ?? [];
}

const getStartEndDateOfSameDay = (date: any) => {
  if (!date || !date.isValid || !date.isValid())
    return { startTime: null, endTime: null };

  // Ensure the date is in dayjs format
  const dayjsDate = dayjs(date);

  const startTime = dayjsDate.startOf("day").utc().toISOString();

  // Set endTime (end of the day in UTC) (selected day is 7 then it will return 7.18.30 which is 7's night 12 time)
  const endTime = dayjsDate.add(1, "day").startOf("day").utc().toISOString();

  return { startTime, endTime };
};

const TimeSlotPicker = ({
  formik,
  // label,
  selectedDateTime,
  onChange,
  openDateCalendar,
  toggleDateCalendar,
  providerUuid,
  selectedLocation,
  appointmentType,
  mode,
  type,
  isTouched,
  isRescheduled,
  bookedSlot: currentBookedSlot,
}: TimeSlotPickerProps) => {
  const { startTime, endTime } = getStartEndDateOfSameDay(selectedDateTime);
  const [newSlots, setNewSlots] = useState<any>([]);
  // const nativeDate = selectedDateTime?.toDate();

  const datePlus3Minutes = dayjs().add(3, "minute").toDate();

  const today = isToday(selectedDateTime);

  const startDate = today ? datePlus3Minutes?.toISOString() : startTime;

  const endDate = convertToUTC(
    endTime ? new Date(endTime) : (undefined as any),
    formik.values?.timezone?.value
  );
  const { data, isLoading, isRefetching }: any = useSlotList({
    pagination: {
      page: 0,
      pageSize: 500,
    },
    providerUuid: providerUuid,
    locationUuid: formik.values?.locationId?.value ?? selectedLocation,
    startDate: startDate,
    endDate: endDate,
    appointmentType,
    availabilityMode: mode,
    xTENANTID: getStoredRealm() ?? "",
    disabled: !selectedDateTime,
  });

  // let start = new Date(formik.values.startTime);
  // let end = new Date(formik.values.endTime);

  useEffect(() => {
    if (
      formik.values?.locationId &&
      selectedLocation &&
      formik.values?.locationId !== selectedLocation
    ) {
      setNewSlots(
        uniqBy(
          // findNonBookSlot(
          insertAndSort(data?.data?.content),
          //   data?.data?.content
          // ) ?? [],
          "startTime"
        )
      );
    } else if (data?.data?.content?.length > 0 && type === "rescheduled") {
      // if (!isDaySameWithDate(selectedDateTime, bookSlot)) {
      //   setNewArray(
      //     findNonBookSlot(
      //       insertAndSort(data?.data.content, start, end),
      //       data?.data?.content
      //     ) ?? []
      //   );
      // } else {
      setNewSlots(
        uniqBy(
          insertAndSort(
            data?.data?.content
            // ?? bookedSlot(newSlots, data?.data?.content),
            // start,
            // end
          ),
          "startTime"
        )
      );
      // }
    } else {
      setNewSlots(data?.data?.content);
    }
  }, [data]);

  // function findNonBookSlot(arr1: any[], arr2: any[]) {
  //   const set = new Set(arr2?.map((item) => JSON.stringify(item)));

  //   return arr1?.filter((item) => set.has(JSON.stringify(item)));
  // }

  function insertAndSort(res: any) {
    //guard for null res
    if (!res) return [];

    // let e: any = new Date(endTime);
    // let s: any = new Date(startTime);
    // const duration = e - s / (1000 * 60);

    // const newObject = {
    //   duration: duration,
    //   status: "AVAILABLE",
    //   startTime: new Date(startTime)?.toISOString(),
    //   endTime: new Date(endTime)?.toISOString(),
    //   timeZone: res?.[0]?.timeZone,
    // };

    // const position = res?.findIndex(
    //   (item: any) => new Date(item.startTime) > new Date(startTime)
    // );

    const newArray = [
      // ...res?.slice(0, position),
      // newObject,
      // ...res?.slice(position),
      ...res,
    ];

    // Filtering duplicates based on 'startTime'
    // const filteredData = newArray?.reduce((acc: any, current: any) => {
    //   if (!acc.some((item: any) => item?.startTime === current?.startTime)) {
    //     acc.push(current);
    //   }
    //   return acc;
    // }, []);

    // booked slot
    // const bookedTime =currentBookedSlot
    //   bookedSlot(newArray, data?.data?.content)?.startTime ?? null;

    const filteredData = newArray?.filter(
      (slot) => slot?.startTime !== currentBookedSlot
    );

    return filteredData;
  }

  const slotSelectedTime = formik.values?.duration
    ? "  (" +
      formatTime(formik.values?.startTime, {
        // withSeconds: true,
        timezone: formik.values?.timezone?.value ?? undefined,
      }) +
      " - " +
      formatTime(formik.values?.endTime, {
        // withSeconds: true,
        timezone: formik.values?.timezone?.value ?? undefined,
      }) +
      ")"
    : "";

  const getTimeZoneDate = (d: any) =>
    formatDate(d, formik.values?.timezone?.value);

  const selectedTimezoneDate = getTimeZoneDate(selectedDateTime);
  const isNoSlotAvailable = newSlots?.every((slot: any) => {
    return !(
      selectedTimezoneDate === getTimeZoneDate(slot?.startTime) ||
      selectedTimezoneDate === getTimeZoneDate(slot?.endTime)
    );
  });

  return (
    <Grid container>
      {isRescheduled && (
        <Grid item xs={12}>
          <Input
            label={"Date & Time"}
            required
            error={
              isTouched.current ? "Date & Time is a required field" : false
            }
            textFieldProps={{
              placeholder: "Choose",
              onBlurCapture: () => {
                isTouched.current = !selectedDateTime;
              },
              value: selectedDateTime
                ? formatDate(selectedDateTime.toDate()) + slotSelectedTime
                : "",
              InputProps: {
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton onClick={toggleDateCalendar}>
                      <EventOutlinedIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
      )}
      {openDateCalendar && (
        <Grid item xs={12} container>
          <Grid item xs={5}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                views={["year", "month", "day"]}
                minDate={dayjs()}
                value={selectedDateTime}
                onChange={(date) => {
                  const navigate = date?.toDate();
                  const newDate = new Date();

                  if (navigate) {
                    navigate?.setHours(
                      newDate.getHours(),
                      newDate.getMinutes(),
                      newDate.getSeconds(),
                      0
                    );

                    onChange(dayjs(navigate));
                  }
                  isTouched.current = false;
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid xs={7} mt={2} width={"100%"}>
            {!!selectedDateTime && !!newSlots?.length ? (
              <ScrollArea height={"300px"} width={"100%"}>
                <Flex flexWrap={"wrap"} gap={1} width={"100%"}>
                  {newSlots?.map((slot: any) => {
                    const startTimezoneSlot = getTimeZoneDate(slot?.startTime);
                    const endTimezoneSlot = getTimeZoneDate(slot?.endTime);

                    return bookedSlot(newSlots, data?.data?.content)
                      ?.startTime !== slot?.startTime ? (
                      <>
                        {(selectedTimezoneDate === startTimezoneSlot ||
                          selectedTimezoneDate === endTimezoneSlot) && (
                          <Box width={"48%"}>
                            <Button
                              color={
                                bookedSlot(newSlots, data?.data?.content)
                                  ?.startTime === slot?.startTime
                                  ? "success"
                                  : undefined
                              }
                              sx={{
                                width: "100%",
                                "&:hover": {
                                  backgroundColor:
                                    bookedSlot(newSlots, data?.data?.content)
                                      ?.startTime === slot?.startTime
                                      ? "#2e7d32"
                                      : undefined,
                                },
                              }}
                              variant={
                                (formik.values.startTime === slot.startTime &&
                                  formik.values.endTime === slot.endTime) ||
                                bookedSlot(newSlots, data?.data?.content)
                                  ?.startTime === slot?.startTime
                                  ? "contained"
                                  : "outlined"
                              }
                              onClick={() => {
                                formik.setFieldValue(
                                  "startTime",
                                  slot.startTime
                                );
                                formik.setFieldValue("endTime", slot.endTime);
                                formik.setFieldValue("duration", slot.duration);

                                const navigate = selectedDateTime?.toDate();

                                if (navigate)
                                  navigate?.setHours(
                                    new Date(slot.startTime).getHours(),
                                    new Date(slot.startTime).getMinutes(),
                                    0,
                                    0
                                  );

                                onChange(dayjs(navigate));

                                const providerTimezone =
                                  TIME_ZONE_WITH_OFFSET_LIST?.find(
                                    (it) => it.curType === slot?.timeZone
                                  );

                                if (!formik.values?.timezone?.value)
                                  formik.setFieldValue(
                                    "timezone",
                                    providerTimezone
                                  );

                                toggleDateCalendar();
                              }}
                            >
                              {/* {JSON.stringify(slot?.startTime)} */}
                              <Text>{`${formatTime(slot.startTime, {
                                // withSeconds: true,
                                timezone:
                                  formik.values?.timezone?.value ?? undefined,
                              })} - ${formatTime(slot.endTime, {
                                // withSeconds: true,
                                timezone:
                                  formik.values?.timezone?.value ?? undefined,
                              })}`}</Text>
                            </Button>
                          </Box>
                        )}
                      </>
                    ) : undefined;
                  })}
                </Flex>

                {isNoSlotAvailable && !isLoading && !isRefetching && (
                  <Flex
                    height={"inherit"}
                    width={"inherit"}
                    alignItems={"center"}
                    justifyContent={"center"}
                  >
                    <Text>{"No Slots Available"}</Text>
                  </Flex>
                )}
              </ScrollArea>
            ) : (
              <Flex
                height={"100%"}
                width={"100%"}
                alignItems={"center"}
                justifyContent={"center"}
              >
                <Text>
                  {!selectedDateTime
                    ? "Please Select Date"
                    : "No Slots Available"}
                </Text>
              </Flex>
            )}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default TimeSlotPicker;
